import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { propType } from 'graphql-anywhere';
import { useApolloClient } from '@apollo/client';
import {
  flatten,
  isEqualWith,
  sortBy,
  unionWith,
} from 'lodash';

import { SEGMENT_PAGE_CONTEXTS } from '../constants';

import credentialFragment from '../fragments/credentialFragment';
import dokkioUserFragment from '../fragments/dokkioUserFragment';
import integrationFragment from '../fragments/integrationFragment';
import integrationItemFragment from '../fragments/integrationItemFragment';
import integrationItemHierarchyFragment from '../fragments/integrationItemHierarchyFragment';
import remoteItemFromProviderFragment from '../fragments/remoteItemFromProviderFragment';
import googleDriveSharedDriveFragment from '../fragments/googleDriveSharedDriveFragment';

import QUERY_BOX_REMOTE_ITEMS_PAGE from '../queries/queryBoxRemoteItemsPage';
import QUERY_DROPBOX_REMOTE_ITEMS_PAGE from '../queries/queryDropboxRemoteItemsPage';
import QUERY_GOOGLE_DRIVE_REMOTE_ITEMS_PAGE from '../queries/queryGoogleDriveRemoteItemsPage';
import QUERY_GOOGLE_DRIVE_SHARED_DRIVES_PAGE from '../queries/queryGoogleDriveSharedDrivesPage';
import QUERY_INTEGRATION_ITEMS_PAGE from '../queries/queryIntegrationItemsPage';
import QUERY_MICROSOFT_TEAMS_REMOTE_ITEMS_PAGE from '../queries/queryMicrosoftTeamsRemoteItemsPage';
import QUERY_ONE_DRIVE_REMOTE_ITEMS_PAGE from '../queries/queryOneDriveRemoteItemsPage';
import QUERY_SLACK_REMOTE_CHANNELS_PAGE from '../queries/querySlackRemoteChannelsPage';

import usePrevious from '../hooks/usePrevious';

import areObjectsEqual from '../utils/areObjectsEqual';

import { DataContext } from './Data';
import IntegrationsStatusBanner from './IntegrationsStatusBanner';
import TreeViewContainer from './TreeViewContainer';

const QUERY_INTEGRATION_ITEM_HIERARCHY_RECURSIVE = gql`
  query integrationItem(
    $integrationID: DokkioID!
    $remoteID: ID!
  ) {
      integrationItem(
        integrationID: $integrationID
        remoteID: $remoteID
    ) {
      ...integrationItemFragment
      hierarchyRecursive {
        ...integrationItemHierarchyFragment
        parentIntegrationItem {
          ...integrationItemFragment
        }
      }
    }
  }
  ${integrationItemFragment}
  ${integrationItemHierarchyFragment}
`;

export const toggleObject = (array, object) => {
  const existingIndex = array.findIndex(element => areObjectsEqual(element, object));
  if (existingIndex === -1) {
    return array.concat(object);
  }
  return [...array.slice(0, existingIndex), ...array.slice(existingIndex + 1)];
};

export const findParentSharedDriveID = (parent) => {
  let parentToSearch = parent;
  while (parentToSearch && !parentToSearch.sharedDriveID) {
    parentToSearch = parentToSearch.parent;
  }
  return parentToSearch ? parentToSearch.sharedDriveID : null;
};

export const TreeView = ({
  dataUpdatedAt,
  isObjectDisplayed,
  isObjectExpandable,
  isObjectSelectable: rawIsObjectSelectable,
  onFailure,
  onUpdateExpandedObjects,
  onUpdateSelectedObjects,
  renderAdditionalContent,
  renderPopoverContent: rawRenderPopoverContent,
  rootObject,
  segmentPageCtxt,
  selectedObjects,
  selectionMode,
}) => {
  const {
    credentialByID,
    integrationByID,
  } = useContext(DataContext);
  const { query } = useApolloClient();
  const [expandedObjects, setExpandedObjects] = useState(rootObject ? [rootObject] : []);
  const prevSelectedObjects = usePrevious(selectedObjects);

  const isObjectCredentialBroken = object => object
    && object.integrationID
    && integrationByID[object.integrationID]
    && integrationByID[object.integrationID].credentialID
    && !!credentialByID[integrationByID[object.integrationID].credentialID].errorCode;

  const isObjectExpanded = object => expandedObjects
    .some(element => areObjectsEqual(element, object));

  const isObjectSelected = object => (selectionMode === 'none'
    ? false
    : selectedObjects.some(element => areObjectsEqual(element, object)));

  const isObjectSelectable = object => (isObjectCredentialBroken(object)
    ? false
    : rawIsObjectSelectable(object));

  const renderPopoverContent = (object) => {
    const popoverContent = rawRenderPopoverContent(object);
    if (popoverContent) {
      return popoverContent;
    }

    if (isObjectCredentialBroken(object)) {
      return 'Dokkio has lost access to this content source';
    }

    return null;
  };

  const onToggleExpanded = (object) => {
    if (!isObjectExpandable(object) || !object) {
      return;
    }

    const newExpandedObjects = toggleObject(expandedObjects, object);
    setExpandedObjects(newExpandedObjects);
    const isExpanded = newExpandedObjects.includes(object);
    onUpdateExpandedObjects({
      expandedObjects: newExpandedObjects,
      object,
      isExpanded,
    });
  };

  const onSelect = (e, object) => {
    if (!isObjectSelectable(object)) {
      return;
    }

    const newSelectedObjects = selectionMode === 'multi'
      ? toggleObject(selectedObjects, object)
      : [object];
    const isSelected = newSelectedObjects.includes(object);
    onUpdateSelectedObjects({
      selectedObjects: newSelectedObjects,
      object,
      isSelected,
    });
  };

  const expandFoldersTo = async (objects) => {
    let newExpandedObjects = expandedObjects;
    for (const { dokkioClass, integrationID, remoteID } of objects) {
      if (dokkioClass === 'integration_item') {
        const {
          data: { integrationItem: { hierarchyRecursive } },
        } = await query({
          query: QUERY_INTEGRATION_ITEM_HIERARCHY_RECURSIVE,
          variables: {
            integrationID,
            remoteID,
          },
          fetchPolicy: 'no-cache',
        });

        const recursiveParentItems = flatten(hierarchyRecursive)
          .map(hierarchyItem => hierarchyItem.parentIntegrationItem)
          .filter(Boolean);
        newExpandedObjects = unionWith(newExpandedObjects, recursiveParentItems, areObjectsEqual);
      }
    }

    setExpandedObjects(newExpandedObjects);
  };

  const fetchAllIntegrationItems = async ({ integrationID, parentRemoteID }) => {
    let integrationItems = [];
    let total;
    do {
      const {
        data: {
          integrationItemsPage: {
            integrationItems: newIntegrationItems,
            total: newTotal,
          },
        },
      } = await query({
        query: QUERY_INTEGRATION_ITEMS_PAGE,
        variables: {
          integrationID,
          parentRemoteID,
          offset: integrationItems.length,
        },
        fetchPolicy: 'no-cache',
      });
      integrationItems = integrationItems.concat(newIntegrationItems);
      total = newTotal;
    } while (total > integrationItems.length);

    return integrationItems;
  };

  const fetchAllDropboxRemoteItems = async ({ credentialID, parentFolderRemoteID }) => {
    let remoteItems = [];
    let hasMore;
    let cursor;
    do {
      const {
        data: {
          dropboxRemoteItemsPage: {
            remoteItems: newRemoteItems,
            hasMore: newHasMore,
            cursor: newCursor,
          },
        },
      } = await query({
        query: QUERY_DROPBOX_REMOTE_ITEMS_PAGE,
        variables: {
          credentialID,
          cursor,
          parentFolderRemoteID,
        },
        fetchPolicy: 'no-cache',
      });
      remoteItems = remoteItems.concat(newRemoteItems);
      hasMore = newHasMore;
      cursor = newCursor;
    } while (hasMore);
    return remoteItems;
  };

  const fetchAllGoogleDriveSharedDrives = async ({ credentialID }) => {
    let sharedDrives = [];
    let pageToken;
    do {
      const {
        data: {
          googleDriveSharedDrivesPage: {
            sharedDrives: newSharedDrives,
            pageToken: newPageToken,
          },
        },
      } = await query({
        query: QUERY_GOOGLE_DRIVE_SHARED_DRIVES_PAGE,
        variables: {
          credentialID,
          pageToken,
        },
        fetchPolicy: 'no-cache',
      });
      sharedDrives = sharedDrives.concat(newSharedDrives);
      pageToken = newPageToken;
    } while (pageToken);
    return sharedDrives;
  };

  const fetchAllGoogleDriveRemoteItems = async ({
    credentialID,
    parentFolderRemoteID,
    sharedDriveID,
    sharedWithMe,
  }) => {
    let remoteItems = [];
    let pageToken;
    do {
      const {
        data: {
          googleDriveRemoteItemsPage: {
            remoteItems: newRemoteItems,
            nextPageToken,
          },
        },
      } = await query({
        query: QUERY_GOOGLE_DRIVE_REMOTE_ITEMS_PAGE,
        variables: {
          credentialID,
          pageToken,
          parentFolderRemoteID,
          sharedDriveID,
          sharedWithMe,
        },
        fetchPolicy: 'no-cache',
      });
      remoteItems = remoteItems.concat(newRemoteItems);
      pageToken = nextPageToken;
    } while (pageToken);
    return remoteItems;
  };

  const fetchAllBoxRemoteItems = async ({ credentialID, parentFolderRemoteID }) => {
    let remoteItems = [];
    let marker;
    do {
      const {
        data: {
          boxRemoteItemsPage: {
            remoteItems: newRemoteItems,
            nextMarker,
          },
        },
      } = await query({
        query: QUERY_BOX_REMOTE_ITEMS_PAGE,
        variables: {
          credentialID,
          marker,
          parentFolderRemoteID,
        },
        fetchPolicy: 'no-cache',
      });
      remoteItems = remoteItems.concat(newRemoteItems);
      marker = nextMarker;
    } while (marker);
    return remoteItems;
  };

  const fetchAllOneDriveRemoteItems = async ({ credentialID, parentFolderRemoteID }) => {
    let remoteItems = [];
    let token;
    do {
      const {
        data: {
          oneDriveRemoteItemsPage: {
            remoteItems: newRemoteItems,
            nextToken,
          },
        },
      } = await query({
        query: QUERY_ONE_DRIVE_REMOTE_ITEMS_PAGE,
        variables: {
          credentialID,
          token,
          parentFolderRemoteID,
        },
        fetchPolicy: 'no-cache',
      });
      remoteItems = remoteItems.concat(newRemoteItems);
      token = nextToken;
    } while (token);
    return remoteItems;
  };

  const fetchAllMicrosoftTeamsRemoteItems = async ({ credentialID, parentFolderRemoteID }) => {
    let remoteItems = [];
    let token;
    do {
      const {
        data: {
          microsoftTeamsRemoteItemsPage: {
            remoteItems: newRemoteItems,
            nextToken,
          },
        },
      } = await query({
        query: QUERY_MICROSOFT_TEAMS_REMOTE_ITEMS_PAGE,
        variables: {
          credentialID,
          token,
          parentFolderRemoteID,
        },
        fetchPolicy: 'no-cache',
      });
      remoteItems = remoteItems.concat(newRemoteItems);
      token = nextToken;
    } while (token);
    return remoteItems;
  };

  const fetchAllSlackChannels = async ({ credentialID }) => {
    let slackChannels = [];
    let cursor;
    do {
      const {
        data: {
          slackRemoteChannelsPage: {
            channels: newSlackChannels,
            nextCursor,
          },
        },
      } = await query({
        query: QUERY_SLACK_REMOTE_CHANNELS_PAGE,
        variables: {
          credentialID,
          cursor,
        },
        fetchPolicy: 'no-cache',
      });
      slackChannels = slackChannels.concat(newSlackChannels);
      cursor = nextCursor;
    } while (cursor);
    return slackChannels;
  };

  const fetchChildrenForObject = async (parent) => {
    if (parent === null || parent.dokkioClass === 'integration' || parent.dokkioClass === 'integration_item') {
      let integrationID;
      let parentRemoteID;
      if (parent === null) {
        integrationID = null;
        parentRemoteID = null;
      } else if (parent.dokkioClass === 'integration') {
        integrationID = parent.id;
        parentRemoteID = parent.id;
      } else if (parent.dokkioClass === 'integration_item') {
        ({ integrationID } = parent);
        parentRemoteID = parent.remoteID;
      }

      const remoteItems = await fetchAllIntegrationItems({ integrationID, parentRemoteID });
      return remoteItems;
    }

    if ([
      'credential',
      'remote_item_from_provider',
      'google_drive_shared_drive',
      'google_drive_my_drive',
      'google_drive_shared_with_me',
    ].includes(parent.dokkioClass)) {
      const credentialID = parent.dokkioClass === 'credential' ? parent.id : parent.credentialID;
      const { credentialOAuth: { credentialOAuthType } } = credentialByID[credentialID];
      const parentFolderRemoteID = parent.remoteID || null;
      let remoteItems = [];

      const isDropboxCredential = credentialOAuthType.startsWith('dropbox');
      const isBoxCredential = credentialOAuthType.startsWith('box');
      const isGoogleDriveCredential = credentialOAuthType.startsWith('google_drive');
      const isOneDriveCredential = credentialOAuthType.startsWith('onedrive');
      const isMicrosoftTeamsCredential = credentialOAuthType.startsWith('microsoft_teams');
      const isSlackCredential = credentialOAuthType.startsWith('slack');

      try {
        if (isDropboxCredential) {
          remoteItems = await fetchAllDropboxRemoteItems({
            credentialID,
            parentFolderRemoteID,
          });
        } else if (isBoxCredential) {
          remoteItems = await fetchAllBoxRemoteItems({
            credentialID,
            parentFolderRemoteID,
          });
        } else if (isGoogleDriveCredential) {
          if (parent.dokkioClass === 'credential') {
            remoteItems = await fetchAllGoogleDriveSharedDrives({ credentialID });
          } else {
            remoteItems = await fetchAllGoogleDriveRemoteItems({
              credentialID,
              parentFolderRemoteID,
              sharedDriveID: findParentSharedDriveID(parent),
              sharedWithMe: parent.dokkioClass === 'google_drive_shared_with_me',
            });
          }
        } else if (isOneDriveCredential) {
          remoteItems = await fetchAllOneDriveRemoteItems({
            credentialID,
            parentFolderRemoteID,
          });
        } else if (isMicrosoftTeamsCredential) {
          remoteItems = await fetchAllMicrosoftTeamsRemoteItems({
            credentialID,
            parentFolderRemoteID,
          });
        } else if (isSlackCredential) {
          remoteItems = await fetchAllSlackChannels({
            credentialID,
            parentFolderRemoteID,
          });
        }
        remoteItems = sortBy(remoteItems, remoteItem => remoteItem.name.toLowerCase());
        if (isGoogleDriveCredential && parent.dokkioClass === 'credential') {
          remoteItems.unshift({
            dokkioClass: 'google_drive_shared_with_me',
            credentialID: parent.id,
            name: 'Shared with me',
          });
          remoteItems.unshift({
            dokkioClass: 'google_drive_my_drive',
            credentialID: parent.id,
            name: 'My Drive',
          });
        }
        return remoteItems;
      } catch (error) {
        onFailure(error);
        throw error;
      }
    }
    return [];
  };

  useEffect(() => {
    if (!isEqualWith(selectedObjects, prevSelectedObjects, areObjectsEqual)) {
      expandFoldersTo(selectedObjects);
    }
  });

  return (
    <React.Fragment>
      {!rootObject && <IntegrationsStatusBanner segmentPageCtxt={segmentPageCtxt} />}
      <TreeViewContainer
        rootObject={rootObject}
        dataUpdatedAt={dataUpdatedAt}
        indentLevel={0}
        fetchChildrenForObject={fetchChildrenForObject}
        isObjectDisplayed={isObjectDisplayed}
        isObjectExpandable={isObjectExpandable}
        isObjectExpanded={isObjectExpanded}
        isObjectSelectable={isObjectSelectable}
        isObjectSelected={isObjectSelected}
        renderAdditionalContent={renderAdditionalContent}
        renderPopoverContent={renderPopoverContent}
        onToggleExpanded={onToggleExpanded}
        onSelect={onSelect}
        segmentPageCtxt={segmentPageCtxt}
        selectionMode={selectionMode}
      />
    </React.Fragment>
  );
};

TreeView.propTypes = {
  dataUpdatedAt: PropTypes.instanceOf(Date),
  isObjectDisplayed: PropTypes.func,
  isObjectExpandable: PropTypes.func,
  isObjectSelectable: PropTypes.func,
  onFailure: PropTypes.func,
  onUpdateExpandedObjects: PropTypes.func,
  onUpdateSelectedObjects: PropTypes.func,
  renderAdditionalContent: PropTypes.func,
  renderPopoverContent: PropTypes.func,
  rootObject: propType(credentialFragment),
  segmentPageCtxt: PropTypes.oneOf(SEGMENT_PAGE_CONTEXTS),
  selectedObjects: PropTypes.arrayOf(PropTypes.oneOfType([
    propType(credentialFragment),
    propType(dokkioUserFragment),
    propType(integrationFragment),
    propType(integrationItemFragment),
    propType(remoteItemFromProviderFragment),
    propType(googleDriveSharedDriveFragment),
    PropTypes.shape({
      dokkioClass: PropTypes.oneOf([
        'google_drive_my_drive',
        'google_drive_shared_with_me',
      ]),
    }),
    PropTypes.shape({
      dokkioClass: PropTypes.string.isRequired,
      integrationID: PropTypes.string.isRequired,
      remoteID: PropTypes.string.isRequired,
    }),
  ])),
  selectionMode: PropTypes.oneOf(['single', 'multi', 'none']),
};

TreeView.defaultProps = {
  dataUpdatedAt: null,
  isObjectDisplayed: () => true,
  isObjectExpandable: () => true,
  isObjectSelectable: () => true,
  onFailure: () => null,
  onUpdateExpandedObjects: () => null,
  onUpdateSelectedObjects: () => null,
  renderAdditionalContent: null,
  renderPopoverContent: null,
  rootObject: null,
  segmentPageCtxt: 'LocateDialog',
  selectedObjects: [],
  selectionMode: 'single',
};

export default TreeView;
